import React, { useEffect, useState } from 'react';
import { subDays, startOfDay, endOfDay, isValid } from 'date-fns';
import {
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DynamicTable } from 'components';
import { axiosInstance } from 'utils';
import { DateUtil } from 'doctivity-shared/utils';
import { UsageOverTimeChart } from 'components/usage/UsageOverTimeChart';
import { UsageBubble } from 'components/usage/UsageBubble';
import { UsageHeatmap } from 'components/usage/UsageHeatmap';
import { UsagePager } from 'components/usage/UsagePager';
import { paginate } from 'components/usage/usageChartUtil';

const HEATMAP_METRICS = [
  { key: 'percent_active', label: '% Active' },
  { key: 'logins', label: 'Logins' },
  { key: 'notes', label: 'Notes' },
  { key: 'page_views', label: 'Pages' },
  { key: 'ai', label: 'AI' },
  { key: 'data_edits', label: 'Edits' },
  { key: 'views', label: 'Views' },
];
const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};
const metricVal = (r, key) => (key === 'ai'
  ? (Number(r.ai_text) || 0) + (Number(r.ai_voice) || 0)
  : Number(r[key]) || 0);

const COMPARE_METRICS = [
  { value: 'active_users', label: 'Active Users' },
  { value: 'percent_active', label: '% Active' },
  { value: 'logins', label: 'Logins' },
  { value: 'notes', label: 'Activity Notes' },
  { value: 'data_edits', label: 'Data Edits' },
  { value: 'views', label: 'Record Views' },
  { value: 'page_views', label: 'Page Views' },
  { value: 'median_session_seconds', label: 'Median Time on Site' },
  { value: 'ai_text', label: 'AI Text' },
  { value: 'ai_voice', label: 'AI Voice' },
];
const minimumStartDate = new Date(2018, 0, 1);

// Clients per page in the bar chart and the heatmap. Both cards page through the
// full client list rather than truncating it — the summary endpoint returns every
// client in one response, so paging is local and costs no extra requests.
// Chosen for readability: past this the bar chart runs out of vertical room and
// the heatmap's row labels start colliding.
// NOTE: the two cards rank differently on purpose — the bar chart ranks by
// whichever metric is selected above, the heatmap always ranks by active users so
// its rows don't reshuffle as you change the selector.
const CLIENTS_PER_PAGE = 15;
// Card height follows the number of rows actually on screen, so a row is the same
// thickness whether the page holds 15 clients or 2. (Sizing the card to the page
// size instead would keep every page the same height, but ApexCharts divides the
// plot area among however many rows it has — a 2-client page would then render two
// enormous bands.) Height = per-row pitch x rows + fixed chrome, where "chrome" is
// everything ApexCharts reserves outside the plot area: axis labels, padding, and
// the heatmap's legend.
//
// Both chrome figures are MEASURED, not estimated — rendered at 2/3/8/15 rows and
// read back off the DOM. Apex holds them exactly constant regardless of row count
// (68px bar, ~92px heatmap), which is what makes this arithmetic work. The row
// figures reproduce the thickness a full 15-row page has today. If you restyle the
// axes or legend, re-measure rather than nudging these by eye.
const BAR_ROW_PX = 30;
const BAR_CHROME_PX = 68;
const HEATMAP_ROW_PX = 39;
const HEATMAP_CHROME_PX = 92;
const chartHeight = (rowCount, rowPx, chromePx) => Math.max(rowCount, 1) * rowPx + chromePx;

// Client-vs-client tab with its own filtering: a metric selector (which
// column ranks the bar chart) plus an independent From/To date range.
export function UsageCompareTab() {
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [metric, setMetric] = useState('active_users');
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState({ limit: 100, offset: 0 });
  // Each chart pages independently — they rank by different things, so a shared
  // page number would point at unrelated clients in the two cards.
  const [barPage, setBarPage] = useState(0);
  const [heatPage, setHeatPage] = useState(0);

  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  useEffect(() => {
    let alive = true;
    axiosInstance
      .post('/UsageAnalytics', { type: 'USAGE_CLIENT_SUMMARY', start: startISO, end: endISO })
      .then((r) => { if (alive) setSummary(r.data); })
      .catch(() => { if (alive) setSummary({ rows: [] }); });
    // A new date range is a different client list — start both cards at page 1
    // rather than stranding the user on a page that may no longer exist.
    setBarPage(0);
    setHeatPage(0);
    return () => { alive = false; };
  }, [startISO, endISO]);

  const rows = (summary && summary.rows) || [];
  const metricDef = COMPARE_METRICS.find((m) => m.value === metric) || COMPARE_METRICS[0];
  const sorted = rows.slice().sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0));
  const barPaged = paginate(sorted, barPage, CLIENTS_PER_PAGE);
  const top = barPaged.items;

  const series = [{ name: metricDef.label, data: top.map((r) => Number(r[metric]) || 0) }];
  const categories = top.map((r) => r.client_name);
  // Pin the value axis to the overall leader so bar lengths mean the same thing
  // on every page — otherwise each page rescales to its own largest client and
  // page 3 reads as busy as page 1.
  const metricMax = sorted.reduce((m, r) => Math.max(m, Number(r[metric]) || 0), 0) || undefined;

  // Value-axis formatting depends on the chosen metric: percent gets a '%',
  // time-on-site gets a duration, everything else is a whole-number count.
  const isPercent = metric === 'percent_active';
  const isDuration = metric === 'median_session_seconds';
  let metricFormatter;
  if (isPercent) metricFormatter = (v) => `${Math.round(v)}%`;
  else if (isDuration) metricFormatter = (v) => fmtDuration(v);
  const metricIsInteger = !isPercent && !isDuration;

  // Health quadrant: x = total users, y = % active, z = overall activity.
  const bubblePoints = rows
    .filter((r) => Number(r.total_users) > 0)
    .map((r) => ({
      x: Number(r.total_users) || 0,
      y: Number(r.percent_active) || 0,
      z: ['logins', 'notes', 'data_edits', 'views', 'ai_text', 'ai_voice']
        .reduce((s, k) => s + (Number(r[k]) || 0), 0),
      name: r.client_name,
    }));

  // Heatmap: clients (ranked by active users) x metrics, color = per-column
  // normalized 0-100, raw value in the tooltip.
  const heatRanked = rows.slice().sort((a, b) => (b.active_users || 0) - (a.active_users || 0));
  const heatPaged = paginate(heatRanked, heatPage, CLIENTS_PER_PAGE);
  const heatClients = heatPaged.items;
  // Normalize against EVERY client, not just the ones on this page. Scoping the
  // max to the page would rescale the color ramp per page, so the best client on
  // a quiet page would render as dark as the overall leader.
  const colMax = {};
  HEATMAP_METRICS.forEach((m) => {
    colMax[m.key] = Math.max(1, ...heatRanked.map((r) => metricVal(r, m.key)));
  });
  const heatSeries = heatClients
    .map((r) => ({
      name: r.client_name,
      data: HEATMAP_METRICS.map((m) => {
        const raw = metricVal(r, m.key);
        return { x: m.label, y: Math.round((raw / colMax[m.key]) * 100), raw };
      }),
    }))
    .reverse();

  const columns = [
    { label: 'Client', key: 'client_name' },
    { label: 'Total Users', key: 'total_users' },
    { label: 'Active Users', key: 'active_users' },
    { label: '% Active', key: 'percent_active', format: (v) => `${v}%` },
    { label: 'Logins', key: 'logins' },
    { label: 'Notes', key: 'notes' },
    { label: 'Page Views', key: 'page_views' },
    { label: 'Median Time', key: 'median_session_seconds', format: (v) => fmtDuration(v) },
    { label: 'Data Edits', key: 'data_edits' },
    { label: 'Views', key: 'views' },
    { label: 'AI Text', key: 'ai_text' },
    { label: 'AI Voice', key: 'ai_voice' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <FormControl style={{ minWidth: 200 }}>
          <InputLabel>Metric</InputLabel>
          <Select
            value={metric}
            label='Metric'
            onChange={(e) => { setMetric(e.target.value); setBarPage(0); }}
          >
            {COMPARE_METRICS.map((m) => (
              <MenuItem value={m.value} key={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 140 }}>
          <DatePicker
            label='From'
            value={startDate}
            minDate={minimumStartDate}
            maxDate={endDate}
            onChange={(d) => isValid(d) && setStartDate(startOfDay(d))}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
        <FormControl style={{ minWidth: 140 }}>
          <DatePicker
            label='To'
            value={endDate}
            minDate={startDate}
            maxDate={new Date()}
            onChange={(d) => isValid(d) && setEndDate(endOfDay(d))}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      </div>

      <UsageOverTimeChart
        type='bar'
        horizontal
        title={`${metricDef.label} by Client`}
        footer={(
          <UsagePager
            page={barPaged.page}
            pageSize={CLIENTS_PER_PAGE}
            total={barPaged.total}
            unit='clients'
            onChange={setBarPage}
          />
        )}
        categories={categories}
        series={series}
        valueFormatter={metricFormatter}
        integer={metricIsInteger}
        valueAxisMax={metricMax}
        height={chartHeight(top.length, BAR_ROW_PX, BAR_CHROME_PX)}
      />

      <UsageBubble
        title='Client Health Quadrant'
        subtitle='x = total users · y = % active · bubble size = overall activity'
        points={bubblePoints}
        xTitle='Total users'
        yTitle='% active'
      />

      <UsageHeatmap
        title='Customer Health Heatmap'
        subtitle='Rows ranked by active users. Color = share of the highest value in each metric column across all clients, so metrics on different scales stay comparable; hover for raw values'
        footer={(
          <UsagePager
            page={heatPaged.page}
            pageSize={CLIENTS_PER_PAGE}
            total={heatPaged.total}
            unit='clients'
            onChange={setHeatPage}
          />
        )}
        series={heatSeries}
        height={chartHeight(heatClients.length, HEATMAP_ROW_PX, HEATMAP_CHROME_PX)}
      />

      <div style={{ marginTop: 16 }}>
        <DynamicTable
          header='All Clients'
          columns={columns}
          data={sorted}
          query={query}
          onQueryChange={setQuery}
          idKey='client_id'
          noDataMessage='No client usage in this range'
        />
      </div>
    </div>
  );
}
